<template>
    <div>
        <div v-if="!showMegaMenu">
            <div v-if="userStore.isStarterUser" class="cursor-pointer" @click="navigateLink">
                <AppLogo width="160" class="mb-2" />
            </div>
            <template v-for="(linkItem, linkIndex) in items" :key="linkItem.title">
                <div v-if="linkItem.hasMegaMenu" class="text-gray-100">
                    <div
                        class="flex py-4 justify-between border-b border-b-gray-850 items-center w-full"
                        @click="handleOpenMegaMenu(linkItem)"
                    >
                        <NuxtLinkLocale :to="linkItem.link" class="text-body-lg text-gray-500 font-bold">
                            {{ linkItem.title }}
                        </NuxtLinkLocale>
                        <Icon name="tabler:chevron-right" />
                    </div>
                </div>

                <div v-else>
                    <div class="flex py-4 justify-between border-b border-b-gray-850 items-center w-full">
                        <NuxtLinkLocale
                            :to="linkItem.link"
                            @click="useMobileMenuOpen().value = false"
                            class="text-body-lg text-gray-500 font-bold w-full h-full"
                            active-class="!text-gray-100"
                        >
                            {{ linkItem.title }}
                        </NuxtLinkLocale>
                    </div>
                    <ul>
                        <AppHeaderSubMenuItem :item="linkItem" />
                    </ul>
                </div>
            </template>
        </div>

        <!-- * mobile mega menu -->
        <TransitionFade :duration="handleDuration">
            <div v-if="showMegaMenu" class="space-y-6">
                <div class="flex items-center space-x-6">
                    <Icon name="tabler:arrow-left" class="text-gray-200" size="24px" @click="showMegaMenu = false" />
                    <p class="text-body-lg font-medium text-gray-200">{{ selectedLinkItem?.title }}</p>
                </div>

                <!-- * selected mega menu accordion -->
                <ul class="space-y-6">
                    <template v-for="(megaMenuItem, megaMenuIndex) in selectedLinkItem?.subMenu">
                        <li @click="navigate(megaMenuItem)">
                            <div
                                role="button"
                                class="flex items-center w-full justify-between pb-2"
                                @click="toggleMegaMenuCollapse(megaMenuIndex)"
                            >
                                <div>
                                    <p
                                        class="text-body-md font-medium"
                                        :class="megaMenuItem.hasMegaMenu ? 'text-gray-500' : 'text-brand-300'"
                                    >
                                        {{ megaMenuItem.title }}
                                    </p>
                                </div>

                                <div v-if="megaMenuItem.hasMegaMenu">
                                    <Icon
                                        :name="
                                            megaMenuIndex === activeMegaMenuIndex
                                                ? 'tabler:chevron-up'
                                                : 'tabler:chevron-down'
                                        "
                                        class="text-gray-700"
                                        size="24px"
                                    />
                                </div>
                            </div>
                            <TransitionExpand v-if="megaMenuItem.hasMegaMenu">
                                <div v-if="megaMenuIndex === activeMegaMenuIndex" class="space-y-2">
                                    <AppHeaderSubMenuItem
                                        :item="megaMenuItem"
                                        :has-mega-menu="selectedLinkItem?.hasMegaMenu"
                                    />
                                    <div>
                                        <NuxtLinkLocale
                                            class="text-brand-300 text-body-md p-2"
                                            :to="megaMenuItem.link"
                                            @click="useMobileMenuOpen().value = false"
                                        >
                                            {{ $t("global_seeAll") }}
                                            <Icon name="tabler:arrow-narrow-right" size="24px" />
                                        </NuxtLinkLocale>
                                    </div>
                                </div>
                            </TransitionExpand>
                        </li>
                        <!-- <hr class="mt-6 last:hidden border-t border-t-gray-850" /> -->
                    </template>
                </ul>
            </div>
        </TransitionFade>
    </div>
</template>

<script setup lang="ts">
import type { IHeaderLinkItem } from "~/types/components/header.type";
import { useUserStore } from "~/store/global/user.store";
const userStore = useUserStore();

const { navigateLink } = useHandleLogoNavigation();

interface IProps {
    items: IHeaderLinkItem[];
}

defineProps<IProps>();

const showMegaMenu = ref(false);
const activeMegaMenuIndex = ref();
const selectedLinkItem = ref<IHeaderLinkItem>();

const handleOpenMegaMenu = (item: IHeaderLinkItem) => {
    showMegaMenu.value = true;
    selectedLinkItem.value = item;
};

const handleDuration = computed(() => {
    if (showMegaMenu.value) {
        return 1000;
    } else return 0;
});

const toggleMegaMenuCollapse = (index: number) => {
    activeMegaMenuIndex.value = index;
};

const localePath = useLocalePath();

const { isMobile } = useDevice();

const showMobileMenu = useMobileMenuOpen();

const navigate = async (item: IHeaderLinkItem) => {
    if (isMobile && !item.hasMegaMenu) {
        await navigateTo(localePath(item.link as string));
        setTimeout(() => {
            const el = document.querySelector(item.elementId as string);
            el?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
            showMobileMenu.value = false;
        }, 1000);
    } else if (item.hasMegaMenu) {
        return;
    } else {
        await navigateTo(localePath(item.link as string));
    }
    return;
};
</script>
